"use client";

import { useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Suspense } from "react";
import { Button, Card, CardBody, CardHeader } from "@heroui/react";
import * as Label from "@radix-ui/react-label";
import { motion } from "framer-motion";
import { Lock, TrendingUp, AlertCircle, ShieldCheck } from "lucide-react";
import { BackgroundBeams } from "@/components/aceternity/background-beams";

function LoginPageInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const from = searchParams.get("from") || "/trading/strategies";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });

      if (res.ok) {
        router.push(from);
        router.refresh();
      } else {
        const data = await res.json();
        setError(data.error || "Invalid password");
        setPassword("");
      }
    } catch {
      setError("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-slate-950 overflow-hidden">
      <BackgroundBeams />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md px-4"
      >
        <Card className="bg-slate-900/80 backdrop-blur-xl border border-slate-800 shadow-2xl">
          <CardHeader className="flex flex-col items-center gap-4 pb-2 pt-8">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.4 }}
              className="p-4 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg shadow-blue-500/25"
            >
              <TrendingUp className="h-8 w-8 text-white" />
            </motion.div>
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white mb-1">Trading Terminal Pro</h1>
              <p className="text-slate-400 text-sm">Private access — authorized users only</p>
            </div>
          </CardHeader>

          <CardBody className="px-8 pb-8 pt-4">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="flex flex-col gap-2">
                <Label.Root
                  htmlFor="password"
                  className="text-sm font-medium text-slate-300 flex items-center gap-2"
                >
                  <Lock className="h-3.5 w-3.5 text-slate-400" />
                  Access Password
                </Label.Root>
                <div className="relative">
                  <input
                    id="password"
                    type="password"
                    placeholder="Enter your trading password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                    required
                    className="w-full h-11 px-4 rounded-lg bg-slate-800/60 border border-slate-700 text-white text-sm placeholder:text-slate-500 outline-none ring-0 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 hover:border-slate-600 transition-all duration-200"
                  />
                </div>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20"
                >
                  <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                  <p className="text-sm text-red-400">{error}</p>
                </motion.div>
              )}

              <Button
                type="submit"
                color="primary"
                variant="shadow"
                className="w-full font-semibold bg-gradient-to-r from-blue-500 to-purple-600"
                size="lg"
                isLoading={loading}
              >
                {loading ? "Authenticating..." : "Access Terminal"}
              </Button>
            </form>

            <div className="mt-6 flex items-center justify-center gap-2 text-slate-600 text-xs">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>Session expires after 12 hours</span>
            </div>
          </CardBody>
        </Card>
      </motion.div>
    </div>
  );
}

// useSearchParams() opts the subtree into client-side rendering; without this
// boundary `next build` fails prerendering /login.
export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginPageInner />
    </Suspense>
  );
}
